const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

export interface Notification {
  type: string;
  title: string;
  message: string;
  timestamp: string;
  status?: string;
  hospitalId?: string;
  referralId?: string;
  isRead?: boolean;
}

// Doctor Types
export interface DoctorSignupData {
  fullName: string;
  email: string;
  password: string;
  specialization: string;
  clinicName: string;
  clinicAddress: string;
  phoneNumber: string;
  licenseNumber: string;
  commissionPct?: number;
  documents: File[];
}

export interface DoctorLoginData {
  identifier: string; // email or phone number
  password: string;
}

export interface DoctorUser {
  id: string;
  fullName: string;
  email: string;
  specialization: string;
  clinicName: string;
  clinicAddress: string;
  phoneNumber: string;
  licenseNumber: string;
  workingDays?: string[];
  workingHours?: {
    from: string;
    to: string;
  };
  createdAt: string;
}

export interface DoctorProfileResponse {
  message: string;
  statusCode: number;
  data: DoctorUser;
}

export interface DoctorUpdateData {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  licenseNumber?: string;
  specialization?: string;
  clinicName?: string;
  clinicAddress?: string;
  workingDays?: string[];
  workingHours?: {
    from: string;
    to: string;
  };
}

// Patient Types
export interface CreatePatientData {
  fullName: string;
  age: number;
  gender: string;
  phone: string;
  email?: string;
  address?: string;
  medicalHistory?: {
    medicalConditions?: string[];
    allergies?: string[];
    currentMedications?: string[];
    chronicDiseases?: string[];
  };
}

export interface Patient {
  id: string;
  patientId?: string;
  fullName: string;
  age: number;
  gender: string;
  phone: string;
  email?: string;
  address?: string;
  pronouns?: string;
  dateOfBirth?: string;
  preferredLanguage?: string;
  medicalHistory?: {
    medicalConditions?: string[];
    allergies?: string[];
    currentMedications?: string[];
    chronicDiseases?: string[];
  };
  createdAt: string;
}

export interface CreatePatientResponse {
  message: string;
  statusCode: number;
  data: Patient;
}

export interface PatientsListResponse {
  message: string;
  statusCode: number;
  data: Patient[];
}

// Visit Types
export interface Visit {
  id: string;
  patientId: string;
  doctorId: string;
  visitDate: string;
  chiefComplaint?: string;
  symptoms?: string[];
  diagnosis?: string;
  treatmentPlan?: string;
  notes?: string;
  followUpDate?: string;
  followUpNotes?: string;
  attachments?: {
    name: string;
    url: string;
    type: string;
  }[];
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
  updatedAt: string;
}

export interface CreateVisitData {
  patientId: string;
  chiefComplaint?: string;
  symptoms?: string[];
  diagnosis?: string;
  treatmentPlan?: string;
  notes?: string;
  followUpDate?: string;
  followUpNotes?: string;
  attachments?: {
    name: string;
    url: string;
    type: string;
  }[];
}

export interface UpdateVisitData {
  chiefComplaint?: string;
  symptoms?: string[];
  diagnosis?: string;
  treatmentPlan?: string;
  notes?: string;
  followUpDate?: string;
  followUpNotes?: string;
  attachments?: {
    name: string;
    url: string;
    type: string;
  }[];
  status?: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
}

export interface PatientProfileResponse {
  message: string;
  statusCode: number;
  data: Patient & {
    visits: Visit[];
    lastVisit: Visit | null;
  };
}

export interface UpdatePatientData {
  // Flat fields for form handling (will be converted to medicalHistory for API)
  allergies?: string;
  currentMedications?: string;
  medicalConditions?: string;
  chronicDiseases?: string;
  // Medical history object for API
  medicalHistory?: {
    medicalConditions?: string[];
    allergies?: string[];
    currentMedications?: string[];
    chronicDiseases?: string[];
  };
  lastVisitDate?: string;
  vitalSigns?: Record<string, any>;
  visitHistory?: Record<string, any>;
  documents?: Record<string, any>;
}

export interface AuthResponse {
  message: string;
  statusCode: number;
  data: {
    doctor: DoctorUser;
    accessToken: string;
    expiresIn: number;
  };
}

import auth from '@/lib/auth';

class ApiError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();

  // 403 is a handled business case (pending approval, rejected, etc.)
  // Return the response normally so callers can handle it gracefully
  if (response.status === 403) {
    return data;
  }

  if (!response.ok) {
    throw new ApiError(
      data.message || 'An error occurred',
      response.status
    );
  }

  return data;
}

// Doctor API functions
export const doctorSignup = async (data: DoctorSignupData): Promise<AuthResponse> => {
  const formData = new FormData();

  formData.append('fullName', data.fullName);
  formData.append('email', data.email);
  formData.append('phoneNumber', data.phoneNumber);
  formData.append('specialization', data.specialization);
  formData.append('clinicName', data.clinicName);
  formData.append('clinicAddress', data.clinicAddress);
  formData.append('licenseNumber', data.licenseNumber);
  formData.append('password', data.password);

  if (data.commissionPct !== undefined) {
    formData.append('commissionPct', String(data.commissionPct));
  }

  if (data.documents && data.documents.length > 0) {
    data.documents.forEach((file: File) => {
      formData.append('documents', file);
    });
  }

  const response = await fetch(`${API_URL}/doctor/signup`, {
    method: 'POST',
    // ⚠️ Content-Type header MAT set karo — browser khud multipart boundary set karega
    body: formData,
  });

  return handleResponse<AuthResponse>(response);
};
export const doctorLogin = async (
  data: DoctorLoginData
): Promise<AuthResponse> => {
  const response = await fetch(`${API_URL}/doctor/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  return handleResponse<AuthResponse>(response);
};

export interface HospitalProfileResponse {
  message: string;
  statusCode: number;
  data: {
    id: string;
    hospitalName: string;
    branchName: string;
    licenseNumber: string;
    branchAddress: string;
    contactPersonName: string;
    contactEmail: string;
    contactPhone: string;
    createdAt: string;
  };
}

export const getHospitalProfile = async (): Promise<HospitalProfileResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/getprofile`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<HospitalProfileResponse>(response);
};

export const getDoctorProfile = async (): Promise<DoctorProfileResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/getprofile`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<DoctorProfileResponse>(response);
};

// export const updateDoctorProfile = async (data: DoctorUpdateData): Promise<DoctorProfileResponse> => {
//   const token = auth.getDoctorToken();
//   if (!token) {
//     console.warn('No doctor token, skipping API call');
//     throw new ApiError('No auth token', 401);
//   }

//   const response = await fetch(`${API_URL}/doctor/updateprofile`, {
//     method: 'PATCH',
//     headers: {
//       'Content-Type': 'application/json',
//       'Authorization': `Bearer ${token}`,
//     },
//     body: JSON.stringify(data),
//   });

//   return handleResponse<DoctorProfileResponse>(response);
// };


export const updateDoctorProfile = async (
  data: FormData
): Promise<DoctorProfileResponse> => {
  const token = auth.getDoctorToken();

  if (!token) {
    console.warn("No doctor token, skipping API call");
    throw new ApiError("No auth token", 401);
  }

  const response = await fetch(`${API_URL}/doctor/updateprofile`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: data,
  });

  return handleResponse<DoctorProfileResponse>(response);
};


export const createPatient = async (data: CreatePatientData): Promise<CreatePatientResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/create-patient`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<CreatePatientResponse>(response);
};

export const getPatients = async (): Promise<PatientsListResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/get-patients`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<PatientsListResponse>(response);
};

// Paginated patients with optional search (page/limit supported by backend)
export interface PatientsPaginatedResponse {
  message: string;
  statusCode: number;
  data: Patient[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getPatientsPaginated = async (
  page: number = 1,
  limit: number = 20,
  search?: string
): Promise<PatientsPaginatedResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search?.trim()) {
    params.append('search', search.trim());
  }

  const response = await fetch(`${API_URL}/doctor/get-patients?${params.toString() || ''}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<PatientsPaginatedResponse>(response);
};


// Search patients
export const searchPatients = async (search: string): Promise<PatientsListResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams();
  if (search) {
    params.append('search', search);
  }

  const response = await fetch(`${API_URL}/doctor/search-patients?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<PatientsListResponse>(response);
};

// Get patient by ID
export const getPatientById = async (patientId: string): Promise<PatientProfileResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/patient/${patientId}?t=${Date.now()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    cache: 'no-store',
  })

  return handleResponse<PatientProfileResponse>(response);
};

// Update patient
export const updatePatient = async (patientId: string, data: UpdatePatientData): Promise<CreatePatientResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/update-patient/${patientId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<CreatePatientResponse>(response);
};

// Create visit
export const createVisit = async (data: CreateVisitData): Promise<{ message: string; statusCode: number; data: Visit }> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/visits`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<{ message: string; statusCode: number; data: Visit }>(response);
};

// Update visit
export const updateVisit = async (visitId: string, data: UpdateVisitData): Promise<{ message: string; statusCode: number; data: Visit }> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/visits/${visitId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<{ message: string; statusCode: number; data: Visit }>(response);
};

// Get patient visits
export const getPatientVisits = async (patientId: string): Promise<{ message: string; statusCode: number; data: Visit[] }> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/patient/${patientId}/visits`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<{ message: string; statusCode: number; data: Visit[] }>(response);
};

// Log document download
export const logDocumentDownload = async (patientId: string, documentName: string): Promise<{ message: string; statusCode: number }> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/patient/${patientId}/log-download`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ documentName }),
  });

  return handleResponse<{ message: string; statusCode: number }>(response);
};

// Notification APIs (Hospital & Doctor)
export interface NotificationsResponse {
  message: string;
  statusCode: number;
  data: {
    notifications: Notification[];
    unreadCount: number;
  };
}

export const getHospitalNotifications = async (): Promise<NotificationsResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/notifications`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<NotificationsResponse>(response);
};

export const markHospitalNotificationsRead = async (): Promise<{ message: string; statusCode: number }> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/notifications/read-all`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<{ message: string; statusCode: number }>(response);
};

export const getDoctorNotifications = async (): Promise<NotificationsResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/notifications`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<NotificationsResponse>(response);
};

export const markDoctorNotificationsRead = async (): Promise<{ message: string; statusCode: number }> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/doctor/notifications/read-all`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<{ message: string; statusCode: number }>(response);
};

// Referral Types

// INTERNAL: referral within Iraq — doctor gets first preference to share the treatment plan.
// EXTERNAL: existing flow, unchanged — hospital only creates the treatment plan.
export type ReferralType = 'INTERNAL' | 'EXTERNAL';

export type ArrivalStatus = 'NOT_ARRIVED' | 'ARRIVED';

export interface ReferralAuditLog {
  id: string;
  referralId: string;
  action: string;
  performedBy: string;
  performedByRole: string;
  oldValue?: string;
  newValue?: string;
  reason?: string;
  createdAt: string;
}

export interface Referral {
  id: string;
  referralId: string;
  status: 'UNDER_TREATMENT' | 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'AWAITING_INFO' | 'INFO_REQUESTED' | 'CLOSED' | 'COMPLETED';
  referralType?: ReferralType;
  arrivalStatus?: ArrivalStatus;
  treatmentType?: 'EMERGENCY' | 'ROUTINE' | 'TRANSFER' | 'FOLLOW_UP';
  clinicalNotes?: string;
  diagnosis?: string;
  attachments?: any;
  doctorId: string;
  patientId: string;
  hospitalId: string;
  infoRequestMessage?: string;
  rejectionReason?: string;
  estimatedCost?: number;
  currency?: string;
  dueDate?: string;
  treatmentPlan?: string;
  treatmentPlanStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  treatmentPlanReason?: string;
  // Doctor-first treatment plan (Internal referrals only). When set, the hospital
  // gets view-only access and cannot add its own plan (see addTreatmentPlan on the backend).
  doctorTreatmentPlan?: string;
  doctorTreatmentPlanAt?: string;
  patient?: Patient;
  hospital?: {
    id: string;
    hospitalName: string;
    branchName: string;
    branchAddress: string;
  };
  doctor?: {
    id: string;
    fullName: string;
    specialization: string;
    clinicName?: string;
  };
  auditLogs?: ReferralAuditLog[];
  createdAt: string;
  updatedAt: string;
}

export interface RespondTreatmentPlanData {
  response: 'ACCEPTED' | 'REJECTED';
  reason?: string;
}

export interface CreateReferralData {
  patientId: string;
  hospitalId: string;
  diagnosis: string;
  clinicalNotes?: string;
  treatmentType?: 'EMERGENCY' | 'ROUTINE' | 'TRANSFER' | 'FOLLOW_UP';
  priorityLevel?: 'LOW' | 'MEDIUM' | 'HIGH';
  // Required — tags the referral as INTERNAL or EXTERNAL for the whole downstream workflow
  // (who may share the treatment plan, notification recipients, etc.)
  referralType: ReferralType;
  attachments?: any;
}

// Doctor's first-preference treatment plan, added right after an Internal referral is
// created. Hits POST /referrals/:id/doctor-treatment-plan. If the doctor shares a plan,
// the hospital only gets view access and cannot add its own (hospital fallback flow).
export interface AddDoctorTreatmentPlanData {
  treatmentPlan: string;
  estimatedCost?: number;
  currency?: string;
  dueDate?: string; // ISO date string
}

export interface UpdateReferralStatusData {
  status: 'UNDER_TREATMENT' | 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'AWAITING_INFO' | 'CLOSED';
  reason?: string;
}

export interface ReferralsResponse {
  message: string;
  statusCode: number;
  data: Referral[];
}

export interface ReferralResponse {
  message: string;
  statusCode: number;
  data: Referral;
}

// Dashboard Stats Types
export interface DashboardStats {
  total: number;
  accepted: number;
  rejected: number;
  pending: number;
  infoRequested: number;
  closed: number;
  underTreatment: number;
  totalPatients: number;
}

export interface DashboardStatsResponse {
  message: string;
  statusCode: number;
  data: DashboardStats;
}

// Referral API functions
// export const createReferral = async (data: CreateReferralData): Promise<ReferralResponse> => {
//   const token = auth.getDoctorToken();
//   if (!token) {
//     console.warn('No doctor token, skipping API call');
//     throw new ApiError('No auth token', 401);
//   }

//   const response = await fetch(`${API_URL}/referrals/create-referral`, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//       'Authorization': `Bearer ${token}`,
//     },
//     body: JSON.stringify(data),
//   });

//   return handleResponse<ReferralResponse>(response);
// };

export const createReferral = async (
  data: CreateReferralData,
  files?: File[]
): Promise<ReferralResponse> => {
  const token = auth.getDoctorToken()

  if (!token) {
    console.warn("No doctor token, skipping API call")
    throw new ApiError("No auth token", 401)
  }

  const formData = new FormData()

  formData.append("patientId", data.patientId)
  formData.append("hospitalId", data.hospitalId)
  formData.append("diagnosis", data.diagnosis)

  if (data.clinicalNotes) {
    formData.append(
      "clinicalNotes",
      data.clinicalNotes
    )
  }

  formData.append(
    "treatmentType",
    data.treatmentType || "ROUTINE"
  )

  formData.append("priorityLevel", data.priorityLevel || "MEDIUM")
  formData.append("referralType", data.referralType)

  // Attach actual files
  files?.forEach((file) => {
    formData.append(
      "attachments",
      file,
      file.name
    )
  })

  const response = await fetch(
    `${API_URL}/referrals/create-referral`,
    {
      method: "POST",

      headers: {
        Authorization: `Bearer ${token}`,
      },

      /*
       * IMPORTANT:
       * Do NOT set Content-Type manually.
       * Browser will automatically set:
       * multipart/form-data; boundary=...
       */
      body: formData,
    }
  )

  return handleResponse<ReferralResponse>(
    response
  )
}

export const getDoctorReferrals = async (page: number = 1, limit: number = 10, search: string = '', status: string = ''): Promise<ReferralsResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search) params.append('search', search);
  if (status) params.append('status', status);

  const response = await fetch(`${API_URL}/referrals/doctor-referrals?${params.toString() || ''}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<ReferralsResponse>(response);
};

export const getHospitalReferrals = async (page: number = 1, limit: number = 10, search: string = '', status: string = ''): Promise<ReferralsResponse> => {
  const rawToken = typeof window !== 'undefined' ? localStorage.getItem('hospital_access_token') : null;
  const rawExpiry = typeof window !== 'undefined' ? localStorage.getItem('hospital_token_expiry') : null;
  const expiryMs = rawExpiry ? parseInt(rawExpiry, 10) : NaN;
  const msLeft = Number.isFinite(expiryMs) ? expiryMs - Date.now() : null;

  const token = auth.getHospitalToken();
  console.log('[auth-debug] getHospitalReferrals', {
    tokenPresent: !!token,
    rawTokenPresent: !!rawToken,
    msLeft,
    rawTokenLen: rawToken?.length ?? 0,
    tokenLen: token?.length ?? 0,
  });

  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search) params.append('search', search);
  if (status) params.append('status', status);

  const response = await fetch(`${API_URL}/referrals/hospital-referrals?${params.toString() || ''}`, {


    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<ReferralsResponse>(response);
};

// Get single referral by ID (Hospital)
export const getReferralById = async (referralId: string): Promise<ReferralResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/hospitalreferral/${referralId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<ReferralResponse>(response);
};

// Get single referral by ID (Doctor)
export const getDoctorReferralById = async (referralId: string): Promise<ReferralResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/doctorreferral/${referralId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<ReferralResponse>(response);
};

export const updateReferralStatus = async (
  referralId: string,
  data: UpdateReferralStatusData
): Promise<ReferralResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/${referralId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<ReferralResponse>(response);
};


const getReferralAuthToken = (): string => {
  const doctorToken = auth.getDoctorToken();

  if (doctorToken) {
    return doctorToken;
  }

  const hospitalToken = auth.getHospitalToken();

  if (hospitalToken) {
    return hospitalToken;
  }

  throw new ApiError("No auth token", 401);
};
export const uploadReferralDocuments = async (
  referralId: string,
  files: File[],
): Promise<any> => {
  const token = getReferralAuthToken();

  const formData = new FormData();

  files.forEach((file) => {
    formData.append("files", file, file.name);
  });

  const response = await fetch(
    `${API_URL}/referrals/${referralId}/documents`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );

  return handleResponse(response);
};
export const removeReferralDocument = async (
  referralId: string,
  key: string,
): Promise<any> => {
  const token = getReferralAuthToken();

  const response = await fetch(
    `${API_URL}/referrals/${referralId}/documents`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        key,
      }),
    },
  );

  return handleResponse(response);
};

export const getDoctorReferralStats = async (): Promise<{
  message: string;
  statusCode: number;
  data: {
    total: number;
    pending: number;
    underTreatment: number;
    accepted: number;
    completed: number;
    rejected: number;
    infoRequested: number;
  };
}> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/doctor-referral-stats`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse(response);
};

// Hospital Types
export interface Hospital {
  id: string;
  hospitalName: string;
  branchName: string;
  branchAddress: string;
  licenseNumber: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  createdAt: string;
}

export interface HospitalsResponse {
  message: string;
  statusCode: number;
  data: {
    hospitals: Hospital[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

// Hospital Types for Auth
export interface HospitalSignupData {
  hospitalName: string;
  branchName: string;
  licenseNumber: string;
  branchAddress: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  password: string;
  commissionPct?: number;
}

export interface HospitalLoginData {
  identifier: string;
  password: string;
}

export interface HospitalUser {
  id: string;
  hospitalName: string;
  branchName: string;
  branchAddress: string;
  licenseNumber: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  status: string;
  createdAt: string;
}

export interface HospitalAuthResponse {
  message: string;
  statusCode: number;
  data: {
    hospital: HospitalUser;
    staff?: any;
    accessToken: string;
    expiresIn: number;
  };
}

// Hospital API functions
export const getAllHospitals = async (page: number = 1, limit: number = 100): Promise<HospitalsResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/admin/hospitals?page=${page}&limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<HospitalsResponse>(response);
};

// export const hospitalSignup = async (data: HospitalSignupData): Promise<HospitalAuthResponse> => {
//   const response = await fetch(`${API_URL}/hospital/signup`, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(data),
//   });

//   return handleResponse<HospitalAuthResponse>(response);
// };

export const hospitalSignup = async (
  data: HospitalSignupData,
  files: File[]
): Promise<HospitalAuthResponse> => {
  const formData = new FormData()

  formData.append("hospitalName", data.hospitalName)
  formData.append("branchName", data.branchName)
  formData.append("licenseNumber", data.licenseNumber)
  formData.append("branchAddress", data.branchAddress)
  formData.append("contactPersonName", data.contactPersonName)
  formData.append("contactEmail", data.contactEmail)
  formData.append("contactPhone", data.contactPhone)
  formData.append("password", data.password)

  if (data.commissionPct !== undefined) {
    formData.append("commissionPct", String(data.commissionPct))
  }

  files.forEach((file) => {
    formData.append("documents", file)
  })

  const response = await fetch(`${API_URL}/hospital/signup`, {
    method: "POST",
    body: formData,
  })

  return handleResponse<HospitalAuthResponse>(response)
}


// Multi-document signup functions (FormData for file uploads - backend ready)
export const doctorSignupWithDocs = async (formData: FormData): Promise<AuthResponse> => {
  const response = await fetch(`${API_URL}/doctor/signup`, {
    method: 'POST',
    body: formData,
  });

  return handleResponse<AuthResponse>(response);
};

export const hospitalSignupWithDocs = async (formData: FormData): Promise<HospitalAuthResponse> => {
  const response = await fetch(`${API_URL}/hospital/signup`, {
    method: 'POST',
    body: formData,
  });

  return handleResponse<HospitalAuthResponse>(response);
};

export const hospitalLogin = async (data: HospitalLoginData): Promise<HospitalAuthResponse> => {
  const response = await fetch(`${API_URL}/hospital/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  return handleResponse<HospitalAuthResponse>(response);
};

// Hospital Staff Types 
export interface StaffPermission {
  module: string;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canApprove: boolean;
  canUpload: boolean;
}

export interface HospitalStaff {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  departmentId: string | null;
  department: Department | null;
  role: string;
  status: string;
  createdAt: string;
  permissions?: StaffPermission[];
}

export interface StaffPermissionDto {
  module: string;
  canView?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canApprove?: boolean;
  canUpload?: boolean;
}

export interface CreateStaffDto {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  departmentId: string;
  role: string;
  permissions?: StaffPermissionDto[];
}

export interface UpdateStaffDto {
  fullName?: string;
  email?: string;
  phone?: string;
  departmentId?: string;
  role?: string;
  status?: string;
  permissions?: StaffPermissionDto[];
}

export interface AssignReferralStaffData {
  staffId: string;
}

export interface StaffListResponse {
  message: string;
  statusCode: number;
  data: HospitalStaff[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface StaffResponse {
  message: string;
  statusCode: number;
  data: HospitalStaff;
}

export interface AssignReferralResponse {
  message: string;
  statusCode: number;
  data: Referral;
}

// Hospital Staff API functions (NEW)
export const getHospitalStaffList = async (page: number = 1, limit: number = 10, search = '', status?: string, role?: string, departmentId?: string): Promise<StaffListResponse> => {
  const rawToken = typeof window !== 'undefined' ? localStorage.getItem('hospital_access_token') : null;
  const rawExpiry = typeof window !== 'undefined' ? localStorage.getItem('hospital_token_expiry') : null;
  const expiryMs = rawExpiry ? parseInt(rawExpiry, 10) : NaN;
  const msLeft = Number.isFinite(expiryMs) ? expiryMs - Date.now() : null;

  const token = auth.getHospitalToken();
  console.log('[auth-debug] getHospitalStaffList', {
    tokenPresent: !!token,
    rawTokenPresent: !!rawToken,
    msLeft,
    rawTokenLen: rawToken?.length ?? 0,
    tokenLen: token?.length ?? 0,
  });

  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search) params.append('search', search);
  if (status) params.append('status', status);
  if (role) params.append('role', role);
  if (departmentId) params.append('departmentId', departmentId);

  const response = await fetch(`${API_URL}/hospital/staff-list?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<StaffListResponse>(response);
};

export const createHospitalStaff = async (data: CreateStaffDto): Promise<StaffResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/create-staff`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<StaffResponse>(response);
};

export const getHospitalStaffById = async (staffId: string): Promise<StaffResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/get-staff/${staffId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<StaffResponse>(response);
};

export const updateHospitalStaff = async (staffId: string, data: UpdateStaffDto): Promise<StaffResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/update-staff/${staffId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<StaffResponse>(response);
};

// Hospital Department Types 
export interface Department {
  id: string;
  name: string;
  createdAt: string;
}

export interface DepartmentsResponse {
  message: string;
  statusCode: number;
  data: Department[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateDepartmentData {
  name: string;
}

export const createHospitalDepartment = async (data: CreateDepartmentData): Promise<{ message: string; statusCode: number }> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/create-department`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<{ message: string; statusCode: number }>(response);
};

export const getHospitalDepartments = async (page: number = 1, limit: number = 10, search?: string): Promise<DepartmentsResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search) params.append('search', search);

  const response = await fetch(`${API_URL}/hospital/get-departments?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<DepartmentsResponse>(response);
};

export { ApiError };

export interface HospitalRolesResponse {
  message: string;
  statusCode: number;
  data: string[];
}

export const getHospitalRoles = async (search?: string): Promise<HospitalRolesResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const params = new URLSearchParams();
  if (search) {
    params.append('search', search);
  }

  const response = await fetch(`${API_URL}/hospital/get-roles?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<HospitalRolesResponse>(response);
};

// Assign referral to hospital staff
export const assignReferralToStaff = async (
  referralId: string,
  data: AssignReferralStaffData
): Promise<AssignReferralResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/${referralId}/assign`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<AssignReferralResponse>(response);
};

// Delete hospital staff
export interface DeleteStaffResponse {
  message: string;
  statusCode: number;
}

export const deleteHospitalStaff = async (staffId: string): Promise<DeleteStaffResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/hospital/delete-staff/${staffId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse<DeleteStaffResponse>(response);
};

// Respond to treatment plan (Patient API)
export const respondTreatmentPlan = async (
  referralId: string,
  data: RespondTreatmentPlanData
): Promise<ReferralResponse> => {
  const token = auth.getDoctorToken(); // Use doctor token for now (patient auth TBD)
  if (!token) {
    console.warn('No patient/doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/${referralId}/treatment-plan/respond`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<ReferralResponse>(response);
};

// Doctor adds the treatment plan for an Internal referral (doctor-first preference).
// Once this succeeds, the hospital can only view the plan — it cannot add its own.
export const addDoctorTreatmentPlan = async (
  referralId: string,
  data: AddDoctorTreatmentPlanData
): Promise<ReferralResponse> => {
  const token = auth.getDoctorToken();
  if (!token) {
    console.warn('No doctor token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/${referralId}/doctor-treatment-plan`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<ReferralResponse>(response);
};

// Hospital updates patient arrival status (NOT_ARRIVED | ARRIVED)
export const updateArrivalStatus = async (
  referralId: string,
  data: { arrivalStatus: ArrivalStatus }
): Promise<ReferralResponse> => {
  const token = auth.getHospitalToken();
  if (!token) {
    console.warn('No hospital token, skipping API call');
    throw new ApiError('No auth token', 401);
  }

  const response = await fetch(`${API_URL}/referrals/${referralId}/arrival-status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return handleResponse<ReferralResponse>(response);
};